<template>
    <TableRow :is-selected="isSelected" :is-edited="isEdited" class="text-gray-700"
        @dblclick="toggleExpandRow"
        @mouseenter="handleRowEnter(2000)"
        @mouseleave="handleRowLeave"
    >
        <td :class="['border-r border-b border-gray-200 align-top relative', { 'border-l-[3px] border-l-amber-400': isEdited }]" :style="rowActionsOuterCellStyle" ref="rowBoundary">
            <div :style="rowActionsInnerCellStyle" class="py-0.5">
                <div class="cursor-pointer h-6 flex justify-start items-center space-x-1 px-3">     
                    <input type="checkbox" id="checkbox" class="accent-dark-secondary cursor-pointer" :checked="isSelected" @change="toggleSelected"/>
                    <div class="cursor-pointer p-[1px] rounded hover:bg-indigo-200" @click="toggleExpandRow">
                        <ChevronUpMiniSVG v-if="expandedRow" fill="black" iconClass="h-3.5 w-3.5" class="" />
                        <ChevronDownMiniSVG v-else fill="black" iconClass="h-3.5 w-3.5" class="" />
                    </div>
                </div>
                <div class="flex items-center justify-end pt-1 pr-2">
                    <div class="cursor-pointer p-1.5 rounded-full hover:bg-indigo-200" @click="performRowAction({actionKey: 'openEditForm'})">
                        <ExpandSVG />
                    </div>
                </div>
                <div class="flex items-center justify-end pr-2 pt-0.5">
                    <div
                        class="cursor-pointer p-1.5 rounded-full transition-colors"
                        :class="rowEditMode ? 'bg-amber-200 hover:bg-amber-300' : 'hover:bg-indigo-200'"
                        :title="rowEditMode ? 'Exit row edit mode' : 'Edit this row'"
                        @click="emit('toggle-row-edit', company.id)"
                    >
                        <EditSVG :stroke="rowEditMode ? '#92400e' : 'black'" icon-class="h-3.5 w-3.5" />
                    </div>
                </div>
                <div class="flex flex-col gap-1.5 items-end pr-2 pt-2">
                    <span v-if="archiveReason" >
                        <base-icon-popover-hover
                            class="bg-red-300 border-none  p-[5px]"
                            popoverTitle="Archive Reason"
                            :popoverBody="archiveReason"
                        >
                            <ArchiveSVG icon-class="w-3 h-3" fill="#460809" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="reasonCannotAcquire" class="">
                        <base-icon-popover-hover
                            class="bg-orange-300 border-none p-[5px]"
                            popoverTitle="Can't Acquire"
                            :popoverBody="reasonCannotAcquire"
                        >
                            <BanSVG icon-class="w-3 h-3" fill="#441306" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="companyAcquirer">
                        <base-icon-popover-hover
                            class="bg-amber-300 border-none p-[5px]"
                            popoverTitle="Acquired"
                            :popoverBody="acquisitionInformation"
                        >
                            <CoinSVG icon-class="w-3 h-3" fill="#461901" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="furtherResearchNeeded">
                        <base-icon-popover-hover
                            class="bg-yellow-300 border-none p-[5px]"
                            popoverTitle="Research Needed"
                            :popoverBody="furtherResearchNeeded"
                        >
                            <EyeglassesSVG icon-class="w-3 h-3" fill="#432004" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="webTextStatus == 'success'">
                        <base-icon-popover-hover
                            class="bg-green-300 border-none p-[5px]"
                            popoverTitle="Web Text Status"
                            :popoverBody="successCrawlString"
                        >
                            <SuccessfulWebTextSVG icon-class="w-3 h-3" fill="none" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="webTextStatus == 'failed'">
                        <base-icon-popover-hover
                            class="bg-red-300 border-none p-[5px]"
                            popoverTitle="Web Text Status"
                            popoverBody="Company website crawl failed. Web text not available for full text search."
                        >
                            <FailedWebTextSVG icon-class="w-3 h-3" fill="none" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="webTextStatus == 'none'">
                        <base-icon-popover-hover
                            class="bg-gray-200 border border-gray-400 p-[5px]"
                            popoverTitle="Web Text Status"
                            popoverBody="Company website not crawled. Web text not available for full text search."
                        >
                            <MissingWebTextSVG icon-class="w-3 h-3" fill="none" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="locationStatus == 'success'">
                        <base-icon-popover-hover
                            class="bg-green-300 border-none p-[5px]"
                            popoverTitle="Location Status"
                            popoverBody="Location found. Available on Company Geo Location page."
                        >
                            <LocationSuccesSVG icon-class="w-3 h-3" fill="none" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="locationStatus == 'failed'">
                        <base-icon-popover-hover
                            class="bg-red-300 border-none p-[5px]"
                            popoverTitle="Location Status"
                            popoverBody="Location not found. Not vailable on Company Geo Location page."
                        >
                            <LocationFailSVG icon-class="w-3 h-3" fill="none" />
                        </base-icon-popover-hover>
                    </span>
                    <span v-if="locationStatus == 'none'">
                        <base-icon-popover-hover
                            class="bg-gray-200 border border-gray-400 p-[5px]"
                            popoverTitle="Location Status"
                            popoverBody="Location has not been enriched. Not available on Company Geo Location page."
                        >
                            <LocationNoneSVG icon-class="w-3 h-3" fill="none" />
                        </base-icon-popover-hover>
                    </span>

                </div>
            </div>
        </td>
        <td 
            class="border-r border-b border-gray-200 align-top relative bg-inherit" 
            @mouseenter="showCompanyInfoActions(actionDelay)"
            @mouseleave="hideCompanyInfoActions"
            :style="companyInfoOuterCellStyle"
        >
            <div :style="companyInfoInnerCellStyle" class="px-3 py-1.5 flex flex-col">
                <div class="flex flex-col ">
                    <div class="whitespace-nowrap flex flex-wrap gap-1 pb-1 justify-between tracking-wide items-center">
                        <input v-if="rowEditMode"
                            type="text"
                            :value="getEditValue('company_name', company.company_name ?? '')"
                            @input="emitFieldEdit('company_name', $event.target.value, company.company_name)"
                            @click.stop
                            class="font-semibold text-black w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                            placeholder="Company name"
                        />
                        <div v-else :class="{'bg-amber-200' : isAcquired}" class="font-semibold text-black p-0.5">
                            {{ companyName }}
                        </div>
                    </div>
                    <div class="flex w-full justify-between items-center">
                        <div class="font-semibold text-gray-800 pr-0 shrink-0">
                            DBA:
                        </div>
                        <template v-if="rowEditMode">
                            <input
                                type="text"
                                :value="getEditValue('doing_business_as', company.doing_business_as ?? '')"
                                @input="emitFieldEdit('doing_business_as', $event.target.value, company.doing_business_as)"
                                @click.stop
                                class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                                placeholder="DBA"
                            />
                        </template>
                        <template v-else>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                            <div class="pl-0">
                                {{ companyDBA }}
                            </div>
                        </template>
                    </div>
                    <div class="flex w-full justify-between items-center">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0 shrink-0">
                            PC:
                        </div>
                        <template v-if="rowEditMode">
                            <input
                                type="text"
                                :value="getEditValue('parent_company', company.parent_company ?? '')"
                                @input="emitFieldEdit('parent_company', $event.target.value, company.parent_company)"
                                @click.stop
                                class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                                placeholder="Parent company"
                            />
                        </template>
                        <template v-else>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                            <div class="pl-0">
                                {{ companyParentCompany }}
                            </div>
                        </template>
                    </div>
                    <div class="flex w-full justify-between items-center">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0 shrink-0">
                            INV:
                        </div>
                        <template v-if="rowEditMode">
                            <input
                                type="text"
                                :value="getEditValue('investors', company.investors ?? '')"
                                @input="emitFieldEdit('investors', $event.target.value, company.investors)"
                                @click.stop
                                class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                                placeholder="Investors"
                            />
                        </template>
                        <template v-else>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                            <div class="pl-0">
                                {{ companyInvestors }}
                            </div>
                        </template>
                    </div>
                </div>
                <div id="location-url-section"  class="w-full py-2">
                    <div v-if="rowEditMode" class="flex w-full justify-between items-center">
                        <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 pr-0 shrink-0">
                            URL:
                        </div>
                        <input
                            type="text"
                            :value="getEditValue('website_url', company.website_url ?? '')"
                            @input="emitFieldEdit('website_url', $event.target.value, company.website_url)"
                            @click.stop
                            class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                            placeholder="example.com"
                        />
                    </div>
                    <div v-else-if="effectiveIsNoURL" class="flex w-full justify-between items-center">
                        <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 pr-0">
                            URL:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="text-gray-500 italic">No URL</div>
                    </div>
                    <div v-else-if="companyWebsiteURL" class="flex w-full justify-between items-center">
                        <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 flex items-center gap-1">
                            URL:
                            <CheckMarkSVG v-if="effectiveURLVetted" icon-class="w-3 h-3 text-green-500" title="URL vetted" />
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="break-all line-clamp-1">
                            <a :href="companyWebsiteURL" target="_blank" class="text-indigo-500 hover:underline hover:text-dark-secondary">{{ companyWebsiteURL }}</a>
                        </div>
                    </div>
                    <div v-else class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 pr-0">
                            URL:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0"></div>
                    </div>
                    <template v-if="rowEditMode">
                        <div class="flex flex-col gap-1 pt-1">
                            <div class="font-semibold text-gray-800">Location:</div>
                            <input
                                type="text"
                                :value="getEditValue('address', company.address ?? '')"
                                @input="emitFieldEdit('address', $event.target.value, company.address)"
                                @click.stop
                                class="w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                                placeholder="Address"
                            />
                            <div class="flex gap-1">
                                <input
                                    type="text"
                                    :value="getEditValue('city', company.city ?? '')"
                                    @input="emitFieldEdit('city', $event.target.value, company.city)"
                                    @click.stop
                                    class="flex-grow min-w-0 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                                    placeholder="City"
                                />
                                <input
                                    type="text"
                                    :value="getEditValue('state', company.state ?? '')"
                                    @input="emitFieldEdit('state', $event.target.value, company.state)"
                                    @click.stop
                                    class="w-12 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                                    placeholder="ST"
                                />
                                <input
                                    type="text"
                                    :value="getEditValue('zip', company.zip ?? '')"
                                    @input="emitFieldEdit('zip', $event.target.value, company.zip)"
                                    @click.stop
                                    class="w-16 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                                    placeholder="Zip"
                                />
                            </div>
                            <input
                                type="text"
                                :value="getEditValue('country', company.country ?? '')"
                                @input="emitFieldEdit('country', $event.target.value, company.country)"
                                @click.stop
                                class="w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                                placeholder="Country"
                            />
                        </div>
                    </template>
                    <template v-else>
                        <div class="flex w-full justify-between  pt-1">
                            <div class="font-semibold text-gray-800">
                                Location:
                            </div>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                            <div v-if="companyAddressLineOne">{{ companyAddressLineOne }}</div>
                            <div v-else-if="companyAddressLineTwo"> {{ companyAddressLineTwo }}</div>
                            <div v-else></div>
                        </div>
                        <div v-if="companyAddressLineOne && companyAddressLineTwo" class="w-full flex justify-end">
                            {{ companyAddressLineTwo }}
                        </div>
                    </template>
                </div>
                <div id="zi-location-url-section"  class="w-full">
                    <div v-if="companyZIURL" class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            ZI URL:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="break-all  line-clamp-1 text-indigo-500 hover:underline hover:text-dark-secondary">
                            <a :href="`${companyZIURL}`" target="_blank" class="text-indigo-500 hover:underline hover:text-dark-secondary">{{ companyZIURL }}</a>
                        </div>
                    </div>
                    <div v-else class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                            ZI URL:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">
                            
                        </div>
                    </div>
                    <div class="flex w-full justify-between  pt-1">
                        <div class="font-semibold text-gray-800">
                            ZI Location:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div v-if="ziAddressLineOne">{{ ziAddressLineOne }}</div>
                        <div v-else-if="ziAddressLineTwo"> {{ ziAddressLineTwo }}</div>
                        <div v-else></div>
                    </div>
                    <div v-if="ziAddressLineOne && ziAddressLineTwo" class="w-full flex justify-end">
                        {{ ziAddressLineTwo }}
                    </div>
                </div>
            </div>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform -translate-y-full opacity-0"
                leave-to-class="transform -translate-y-full opacity-0"
                key="zoom-info-actions-menu"
            >
                <div v-if="visibleCompanyInfoActions || rowEditMode"
                    ref="companyInfoActionsMenuBoundary"
                    :class="rowEditMode ? 'w-full flex flex-wrap justify-end gap-1.5 px-2 py-2 pt-3 border-t border-gray-200' : 'w-[calc(100%+6px)] flex flex-wrap justify-end gap-1.5 shadow-md rounded-b-lg px-2 py-2 absolute -left-[3px] z-10 bg-inherit border-b-[3px] border-x-[3px] border-[#9EA0BD]'"
                    :style="companyInfoActionsMenuStyle"
                >
                    <div class="flex flex-wrap justify-end gap-1.5">
                        <button
                            v-if="effectiveURLVetted"
                            @click.stop="handleURLVetClick('remove_url_vetted')"
                            :class="pendingURLVetAction === 'remove_url_vetted' ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-dark-secondary bg-dark-secondary text-white hover:bg-dark-primary hover:text-gray-100'"
                            class="w-36 h-8 px-2 tracking-wide font-medium text-xs border rounded-md whitespace-nowrap flex justify-center items-center transition-colors"
                        >Remove URL Vetted</button>
                        <button
                            v-else-if="effectiveHasURL"
                            @click.stop="handleURLVetClick('mark_url_vetted')"
                            :class="pendingURLVetAction === 'mark_url_vetted' ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-dark-secondary bg-dark-secondary text-white hover:bg-dark-primary hover:text-gray-100'"
                            class="w-36 h-8 px-2 tracking-wide font-medium text-xs border rounded-md whitespace-nowrap flex justify-center items-center transition-colors"
                        >Mark URL Vetted</button>
                        <button
                            v-else
                            @click.stop="handleURLVetClick('mark_no_url_vetted')"
                            :class="pendingURLVetAction === 'mark_no_url_vetted' ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-dark-secondary bg-dark-secondary text-white hover:bg-dark-primary hover:text-gray-100'"
                            class="w-36 h-8 px-2 tracking-wide font-medium text-xs border rounded-md whitespace-nowrap flex justify-center items-center transition-colors"
                        >Mark No URL</button>
                    </div>
                </div>
            </Transition>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform translate-y-[170%] opacity-0"
                leave-to-class="transform translate-y-[170%] opacity-0"
                key="zoom-info-menu-icon"
            >
                <Grid3x2SVG v-if="!visibleCompanyInfoActions && !rowEditMode" :fill="menuIconFill" iconClass="h-3 w-3" class="absolute right-3 bottom-1.5" />
            </Transition>
        </td>
        <td 
            class="border-r border-b border-gray-200 align-top relative bg-inherit"
            @mouseenter="showBusinessInfoActions(actionDelay)"
            @mouseleave="hideBusinessInfoActions"
            :style="businessInfoOuterCellStyle"
        >
            <div 
                @click="showBusinessInfoActions"
                class="w-full flex flex-col items-start justify-start bg-inherit"
                :style="businessInfoInnerCellStyle"
            >
                <div class="flex w-full justify-between items-center">
                    <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0 shrink-0">
                        Rev:
                    </div>
                    <template v-if="rowEditMode">
                        <input
                            type="number"
                            step="0.1"
                            min="0"
                            :value="getEditValue('revenue', company.revenue ?? '')"
                            @input="emitFieldEdit('revenue', $event.target.value, company.revenue)"
                            @click.stop
                            class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                            placeholder="0 ($M)"
                        />
                    </template>
                    <template v-else>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">
                            {{ companyRevenue }}
                        </div>
                    </template>
                </div>
                <div class="flex w-full justify-between items-center">
                    <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0 shrink-0">
                        FTE:
                    </div>
                    <template v-if="rowEditMode">
                        <input
                            type="number"
                            min="0"
                            :value="getEditValue('employee_headcount', company.employee_headcount ?? '')"
                            @input="emitFieldEdit('employee_headcount', $event.target.value, company.employee_headcount)"
                            @click.stop
                            class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                            placeholder="0"
                        />
                    </template>
                    <template v-else>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">
                            {{ companyHeadcount }}
                        </div>
                    </template>
                </div>
                <div id="zoom-info-section"  class=" w-full bg-inherit py-2">
                    <div class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                            ZI Rev:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">
                            {{  zoomInfoRevenue }}
                        </div>
                    </div>
                    <div class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                            ZI FTE:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">
                           {{ zoomInfoHeadcount }}
                        </div>
                    </div>
                </div> 
                <div class="flex flex-col  pb-1 w-full">
                    <div v-if="rowEditMode">
                        <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 mb-0.5">
                            Relevant Business:
                        </div>
                        <textarea
                            :value="getEditValue('relevant_business', company.relevant_business ?? '')"
                            @input="emitFieldEdit('relevant_business', $event.target.value, company.relevant_business)"
                            @click.stop
                            class="w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 resize-y min-h-[52px]"
                            placeholder="Relevant business description"
                        />
                    </div>
                    <div v-else-if="relevantBusiness && expandedRow">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Relevant Business:
                        </div>
                        <div class="whitespace-pre-wrap break-all">
                            {{ relevantBusiness }}
                        </div>
                    </div>
                    <div v-else-if="relevantBusiness" class="flex w-full justify-between">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Relevant Business:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="whitespace-pre-wrap line-clamp-1 break-all">
                            {{ relevantBusiness }}
                        </div>
                    </div>
                    <div v-else class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                            Relevant Business:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">

                        </div>
                    </div>
                </div>
                <div class="flex flex-col w-full  pb-1">
                    <div v-if="rowEditMode">
                        <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 mb-0.5">
                            Acct Manager Notes:
                        </div>
                        <textarea
                            :value="getEditValue('account_manager_research_notes', company.account_manager_research_notes ?? '')"
                            @input="emitFieldEdit('account_manager_research_notes', $event.target.value, company.account_manager_research_notes)"
                            @click.stop
                            class="w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 resize-y min-h-[52px]"
                            placeholder="Account manager research notes"
                        />
                    </div>
                    <div v-else-if="accountManagerNotes && expandedRow">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Acct Manager Notes:
                        </div>
                        <div class="whitespace-pre-wrap break-all">
                            {{ accountManagerNotes }}
                        </div>
                    </div>
                    <div v-else-if="accountManagerNotes" class="flex w-full justify-between">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Acct Manager Notes:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="whitespace-pre-wrap line-clamp-1 break-all">
                            {{ accountManagerNotes }}
                        </div>
                    </div>
                    <div v-else class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                            Acct Manager Notes:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">

                        </div>
                    </div>
                </div>
            </div>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform -translate-y-full opacity-0"
                leave-to-class="transform -translate-y-full opacity-0"
                key="zoom-info-actions-menu"
            >
                <div v-if="visibleBusinessInfoActions || rowEditMode"
                    ref="businessInfoActionsMenuBoundary"
                    :class="rowEditMode ? 'w-full flex flex-wrap justify-end gap-1.5 px-2 py-2 pt-3 border-t border-gray-200' : 'w-[calc(100%+6px)] flex flex-wrap justify-end gap-1.5 shadow-md rounded-b-lg px-2 py-2 absolute -left-[3px] z-10 bg-inherit border-b-[3px] border-x-[3px] border-[#9EA0BD]'"
                    :style="businessInfoActionsMenuStyle"
                >
                    <div v-if="companyHasZI==false" class="flex flex-wrap justify-end gap-1.5">
                        <loading-button
                            :buttonId="`getZoomInfoID-${company.id}`"
                            :buttonResetId="actionButtonResetId"
                            :clickData="{actionKey: 'getZoomInfoID'}"
                            @button-click="performRowAction"
                            class="w-24 h-8 px-2 tracking-wide font-medium">
                            Get ZI ID
                        </loading-button>
                        <disabled-button class="w-24 h-8 px-2 tracking-wide font-medium">
                            Get ZI Basic
                        </disabled-button>
                        <disabled-button class="w-24 h-8 px-2 tracking-wide font-medium">
                            Clear ZI
                        </disabled-button>
                    </div>
                    <div v-else-if="companyHasZI==true" class="flex flex-wrap justify-end gap-1.5">
                        <disabled-button class="w-24 h-8 px-2 tracking-wide font-medium">
                            Get ZI ID
                        </disabled-button>
                        <loading-button
                            :buttonId="`getZoomInfoBasic-${company.id}`"
                            :buttonResetId="actionButtonResetId"
                            :clickData="{actionKey: 'getZoomInfoBasic'}"
                            @button-click="performRowAction"
                            class="w-24 h-8 px-2 tracking-wide font-medium">
                            Get ZI Basic
                        </loading-button>
                        <loading-button
                            :buttonId="`clearZoomInfo-${company.id}`"
                            :buttonResetId="actionButtonResetId"
                            :clickData="{actionKey: 'clearZoomInfo'}"
                            @button-click="performRowAction"
                            class="w-24 h-8 px-2 tracking-wide font-medium">
                            Clear ZI
                        </loading-button>
                    </div>
                </div>
            </Transition>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform translate-y-[170%] opacity-0"
                leave-to-class="transform translate-y-[170%] opacity-0"
                key="zoom-info-menu-icon"
            >
                <Grid3x2SVG v-if="!visibleBusinessInfoActions && !rowEditMode" :fill="menuIconFill" iconClass="h-3 w-3" class="absolute right-3 bottom-1.5" />
            </Transition>
        </td>
        <td 
            class="border-r border-b border-gray-200 align-top relative bg-inherit"
            @mouseenter="showCampaignActions(actionDelay)"
            @mouseleave="hideCampaignActions"
            :style="campaignOuterCellStyle"
        >
            <div
                @click="showCampaignActions"
                class="w-full flex flex-col items-start justify-start bg-inherit" 
                :style="campaignInnerCellStyle"
            >
                <div class="flex w-full justify-between items-center">
                    <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 pr-0 shrink-0">
                        Campaign:
                    </div>
                    <template v-if="rowEditMode && company.campaign_change_requests && company.campaign_change_requests.length === 0">
                        <select
                            :value="getEditValue('campaign_id', company.campaign ? company.campaign.id : '')"
                            @change="emitFieldEdit('campaign_id', $event.target.value ? Number($event.target.value) : null, company.campaign ? company.campaign.id : null)"
                            @click.stop
                            class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                        >
                            <option value="">—</option>
                            <option v-for="c in campaignOptions" :key="c.id" :value="c.id">{{ c.campaign_name }}</option>
                        </select>
                    </template>
                    <template v-else-if="rowEditMode && company.campaign_change_requests && company.campaign_change_requests.length > 0">
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div
                            ref="campaignChangeRequestElement"
                            class="pl-0 cursor-help"
                            @mouseover="showCampaignChangeRequestPopover"
                            @mouseleave="hideCampaignChangeRequestPopover"
                        >{{ companyCampaign }}<span class="text-amber-500 font-bold">*</span></div>
                        <base-popover-hover-small
                            v-if="campaignChangeRequestPopoverVisible"
                            :xPosition="xPosition"
                            :yPosition="yPosition"
                        >
                            <template v-slot:title>Open Change Request</template>
                            <template v-slot:body>
                                This company has an open campaign change request that must be resolved before the campaign can be edited.
                            </template>
                        </base-popover-hover-small>
                    </template>
                    <template v-else>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">{{ companyCampaign }}</div>
                    </template>
                </div>
                <div v-if="!rowEditMode && campaignChanged" class="text-xs text-amber-600 font-medium mt-0.5 mb-1">
                    Requested: {{ requestedCampaign?.campaign_name ?? '—' }}
                </div>
                <div v-if="rowEditMode && campaignChanged" class="flex flex-col gap-1.5 mb-2 mt-0.5 pl-1 border-l-2 border-amber-300">
                    <div class="flex flex-col gap-0.5">
                        <label class="text-xs font-semibold text-gray-700">Change Reason</label>
                        <textarea
                            :value="getEditValue('change_request_reason', '')"
                            @input="emitFieldEdit('change_request_reason', $event.target.value, '')"
                            @click.stop
                            class="w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 resize-y min-h-[40px] text-xs"
                            placeholder="Reason for campaign change"
                        />
                    </div>
                    <label class="flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            :checked="getEditValue('copy_details', false)"
                            @change="emitFieldEdit('copy_details', $event.target.checked, false)"
                            @click.stop
                            class="accent-amber-500 cursor-pointer"
                        />
                        <span class="text-xs text-gray-700">Copy Details</span>
                    </label>
                </div>
                <div class="flex w-full justify-between  pb-2">
                    <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                        Tier:
                    </div>
                    <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                    <div @mouseover="showTierPopover" @mouseleave="hideTierPopover" ref="tierElement" class="pl-0">
                        {{ companyTier }}
                    </div>
                    <base-popover-hover-small 
                        v-if="tierPopoverVisible"
                        :xPosition="xPosition"
                        :yPosition="yPosition"
                    >
                        <template v-slot:title>
                        Previous Tier
                        </template>
                        <template v-slot:body>
                            <div class="flex flex-col">
                                <span>Previous tier: {{company.previous_tier}}</span>
                                <span>Tier last updated: {{tierLastUpdated}}</span>
                            </div>
                        </template>
                    </base-popover-hover-small>
                </div>
                <div class="w-full">
                    <div class="flex w-full justify-between items-center">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0 shrink-0">
                            Client Feedback:
                        </div>
                        <template v-if="rowEditMode">
                            <select
                                :value="getEditValue('client_feedback', company.current_client_feedback ?? '')"
                                @change="emitFieldEdit('client_feedback', $event.target.value, company.current_client_feedback)"
                                @click.stop
                                class="flex-grow ml-1 border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 min-w-0"
                            >
                                <option value="">—</option>
                                <option value="approved">Approved</option>
                                <option value="excluded">Excluded</option>
                                <option value="hold">Hold</option>
                                <option value="pass">Pass</option>
                                <option value="research_further">Research Further</option>
                            </select>
                        </template>
                        <template v-else>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                            <div class="pl-0 capitalize">
                                {{ clientFeedback }}
                            </div>
                        </template>
                    </div>
                    <div class="flex flex-col w-full pb-2">
                    <div v-if="rowEditMode">
                        <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 mb-0.5">
                            Client Feedback Notes:
                        </div>
                        <ClientFeedbackNotesList :company="company" :editable="false" />
                        <!-- Saved as a new note with the rest of the row's edits -->
                        <textarea
                            v-if="company.campaign"
                            :value="getEditValue('client_feedback_notes', '')"
                            @input="emitFieldEdit('client_feedback_notes', $event.target.value, '')"
                            @click.stop
                            class="w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 resize-y min-h-[52px]"
                            placeholder="Add a note"
                        />
                    </div>
                    <div v-else-if="expandedRow">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Client Feedback Notes:
                        </div>
                        <ClientFeedbackNotesList :company="company" @refetch="reloadCompanies" />
                    </div>
                    <div v-else-if="hasClientFeedbackNotes">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Client Feedback Notes:
                        </div>
                        <ClientFeedbackNotesList :company="company" :limit="2" clamp :editable="false" />
                    </div>
                    <div v-else class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                            Client Feedback Notes:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">

                        </div>
                    </div>
                    </div>
                    <ClientFeedbackHistory v-if="expandedRow && !rowEditMode" :company="company" />
                    <div class="flex flex-col w-full">
                        <div v-if="rowEditMode">
                            <div class="whitespace-nowrap font-semibold tracking-wide text-gray-800 mb-0.5">
                                Client Update Notes:
                            </div>
                            <textarea
                                :value="getEditValue('client_update_notes', company.client_update_notes ?? '')"
                                @input="emitFieldEdit('client_update_notes', $event.target.value, company.client_update_notes)"
                                @click.stop
                                class="w-full border border-gray-200 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400 resize-y min-h-[52px]"
                                placeholder="Client update notes"
                            />
                        </div>
                        <div v-else-if="clientUpdateNotes && expandedRow">
                            <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                                Client Update Notes:
                            </div>
                            <div class="whitespace-pre-wrap break-all">
                                {{ clientUpdateNotes }}
                            </div>
                        </div>
                        <div v-else-if="clientUpdateNotes" class="flex w-full justify-between">
                            <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                                Client Update Notes:
                            </div>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                            <div class="whitespace-pre-wrap line-clamp-1 break-all">
                                {{ clientUpdateNotes }}
                            </div>
                        </div>
                        <div v-else class="flex w-full justify-between ">
                            <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                                Client Update Notes:
                            </div>
                            <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                            <div class="pl-0">
                                
                            </div>
                        </div>
                </div>
                <div class="flex flex-col w-full">
                    <div v-if="outreachNotes && expandedRow">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Outreach Notes:
                        </div>
                        <div class="whitespace-pre-wrap break-all">
                            {{ outreachNotes }}
                        </div>
                    </div>
                    <div v-else-if="outreachNotes" class="flex w-full justify-between">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Outreach Notes:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="whitespace-pre-wrap line-clamp-1 break-all">
                            {{ outreachNotes }}
                        </div>
                    </div>
                    <div v-else class="flex w-full justify-between ">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                            Outreach Notes:
                        </div>
                        <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                        <div class="pl-0">
                            
                        </div>
                    </div>
                </div>
                <div class="flex flex-col w-full">
                    <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                        Previous Campaigns:
                    </div>
                    <div class="flex flex-wrap text-xs">
                        <base-pill @click="performRowAction({actionKey: 'openCRMDetails', defaultCampaign: campaign.id})" :title="campaign.campaign_name" v-for="campaign in previousCampaigns" :key="campaign.id" bg-color=" bg-dark-primary" class="truncate m-0.5 hover:cursor-pointer text-white">
                            {{ campaign.campaign_name }}
                        </base-pill>
                    </div>
                </div>
                </div>
            </div>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform -translate-y-full opacity-0"
                leave-to-class="transform -translate-y-full opacity-0"
                key="campaign-actions-menu"
            >
                <div v-if="visibleCampaignActions || rowEditMode"
                    ref="campaignActionsMenuBoundary"
                    :class="rowEditMode ? 'w-full flex flex-wrap justify-end gap-1.5 px-2 py-2 pt-3 border-t border-gray-200' : 'w-[calc(100%+6px)] flex flex-wrap justify-end gap-1.5 shadow-md rounded-b-lg px-2 py-2 absolute -left-[3px] z-10 bg-inherit border-b-[3px] border-x-[3px] border-[#9EA0BD]'"
                    :style="campaignActionsMenuStyle"
                >
                    <div class="grid grid-cols-3 gap-1.5">
                        <button v-for="tier in [1, 2, 3, 4, 5]" :key="tier"
                            @click.stop="handleTierClick(tier)"
                            :class="pendingTier === tier ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-dark-secondary bg-dark-secondary text-white hover:bg-dark-primary hover:text-gray-100'"
                            class="w-full h-8 px-2 tracking-wide font-medium text-xs border rounded-md whitespace-nowrap flex justify-center items-center transition-colors"
                        >Tier {{ tier }}</button>
                        <button
                            @click.stop="handleTierClick('None')"
                            :class="pendingTier === null && pendingTier !== undefined ? 'border-amber-400 bg-amber-50 text-amber-800' : 'border-dark-secondary bg-dark-secondary text-white hover:bg-dark-primary hover:text-gray-100'"
                            class="w-full h-8 px-2 tracking-wide font-medium text-xs border rounded-md whitespace-nowrap flex justify-center items-center transition-colors"
                        >RM</button>
                    </div>
                </div>
            </Transition>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform translate-y-[170%] opacity-0"
                leave-to-class="transform translate-y-[170%] opacity-0"
                key="campaign-menu-icon"
            >
                <Grid3x2SVG v-if="!visibleCampaignActions && !rowEditMode" :fill="menuIconFill" iconClass="h-3 w-3" class="absolute right-3 bottom-1.5" />
            </Transition>
        </td>
        <td
            ref="crmActionsCell"
            class="border-r border-b border-gray-200 align-top relative bg-inherit"
            @mouseenter="showCRMActions(actionDelay)"
            @mouseleave="hideCRMActions"
            :style="crmActionsOuterCellStyle"
        >
            <div 
                @click="showCRMActions"
                class="w-full h-full flex flex-col items-start justify-start" 
                :style="crmActionsInnerCellStyle"
            >
                <div class="flex w-full justify-between">
                    <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                        Status:
                    </div>
                    <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                    <div class="pl-0">
                        {{ currentCRMStatus }}
                    </div>
                </div>
                <div class="flex w-full justify-between pb-2">
                    <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                        Next Action:
                    </div>
                    <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                    <div class="flex space-x-1">
                        <div class="pl-0 whitespace-nowrap">
                            {{ currentCRMNextAction }}
                        </div>
                        <div v-if="currentCRMNextActionDueDate" class="">
                            {{ currentCRMNextActionDueDate }}
                        </div>
                    </div>
                </div>
                <div v-if="crmDetails" class="flex flex-col w-full h-full">
                    <div class="flex w-full justify-between pb-2">
                        <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800">
                            Summary:
                        </div>
                    </div>
                    <div class="w-full h-full pb-2">
                        <CRMDetailsList
                            :company="company"
                            :endIndex="crmDetailsEndIndex"
                        />
                    </div>
                </div>
                <div v-else class="flex w-full justify-between">
                    <div class="whitespace-nowrap font-semibold tracking-wide  text-gray-800 pr-0">
                        CRM Summary:
                    </div>
                    <div class="border-b-2 border-dotted border-gray-400 flex-grow mb-2 mx-1"></div>
                    <div class="pl-0">
                        
                    </div>
                </div>
                <div v-if="numCRMDetailsFromPreviousCampaigns > 0" class="flex">
                    + {{ numCRMDetailsFromPreviousCampaigns }} CRM details in previous campaigns
                </div>
            </div>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform -translate-y-full opacity-0"
                leave-to-class="transform -translate-y-full opacity-0"
                key="crm-actions-menu"
            >
                <div v-if="visibleCRMActions || rowEditMode"
                    ref="crmActionsMenuBoundary"
                    :class="rowEditMode ? 'w-full flex flex-wrap justify-end gap-1.5 px-2 py-2 pt-3 border-t border-gray-200' : 'w-[calc(100%+6px)] flex flex-wrap justify-end gap-1.5 shadow-md rounded-b-lg px-2 py-2 absolute -left-[3px] z-10 bg-inherit border-b-[3px] border-x-[3px] border-[#9EA0BD]'"
                    :style="crmActionsMenuStyle"
                >
                    <div class="grid grid-cols-2 gap-1 lg:gap-3 w-full">
                        <div class="flex w-full flex-col h-full justify-between">
                            <div class="flex flex-col space-y-1">
                                <div>
                                    CRM Status:
                                </div>
                                <select
                                    :value="getEditValue('crm_status', crmStatus)"
                                    @change="emitFieldEdit('crm_status', $event.target.value ? Number($event.target.value) : null, crmStatus)"
                                    @click.stop
                                    class="px-2 py-1 border border-gray-300 cursor-pointer rounded-md"
                                >
                                    <option v-for="option in statusOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                                </select>
                            </div>
                            <div class="pt-2">
                                <button 
                                    @click="performRowAction({actionKey: 'openCRMDetails'})"
                                    class="w-full  bg-none border border-dark-secondary hover:bg-dark-primary hover:text-gray-100 hover:shadow active:bg-dark-primary active:text-gray-400 text-white bg-dark-secondary whitespace-nowrap tracking-wide font-medium px-1 py-1 rounded-md">
                                    All CRM Details
                                </button>
                            </div>
                        </div>
                        <div class="flex w-full flex-col">
                            <div class="flex flex-col space-y-1">
                                <div>
                                    CRM Next Action / Due Date:
                                </div>
                                <select
                                    :value="getEditValue('crm_next_action', crmNextAction)"
                                    @change="emitFieldEdit('crm_next_action', $event.target.value || null, props.company.crm_next_action)"
                                    @click.stop
                                    class="px-2 py-1 border border-gray-300 cursor-pointer rounded-md"
                                >
                                    <option v-for="option in crmNextActionOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                                </select>
                            </div>
                            <div class="flex flex-col space-y-1 h-full">
                                <div class="pt-[11px]">
                                    <input type="date" class="w-full px-2 py-1 border border-gray-300 cursor-pointer rounded-md" :value="getEditValue('crm_next_action_due_date', crmNextActionDueDate)" @change="emitFieldEdit('crm_next_action_due_date', $event.target.value || null, props.company.crm_next_action_due_date)" @click.stop @focus="dueDatePickerFocused = true" @blur="handleDueDatePickerBlur">
                                </div>
                                <div class="flex justify-end w-full pt-2">
                                    <div class="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-1.5 w-full">
                                        <button v-for="opt in [{label:'+3d',days:3},{label:'+1w',days:7},{label:'+2w',days:14},{label:'+3w',days:21},{label:'+1m',days:30},{label:'+2m',days:60},{label:'+3m',days:90}]" :key="opt.label"
                                            @click.stop="emitDueDateOffset(opt.days)"
                                            class="w-full h-8 px-2 tracking-wide font-medium text-xs border border-dark-secondary bg-dark-secondary text-white hover:bg-dark-primary hover:text-gray-100 rounded-md whitespace-nowrap flex justify-center items-center transition-colors"
                                        >{{ opt.label }}</button>
                                        <button
                                            @click.stop="emitFieldEdit('crm_next_action_due_date', null, props.company.crm_next_action_due_date)"
                                            class="w-full h-8 px-2 tracking-wide font-medium text-xs border border-dark-secondary bg-dark-secondary text-white hover:bg-dark-primary hover:text-gray-100 rounded-md whitespace-nowrap flex justify-center items-center transition-colors"
                                        >Clear</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform translate-y-[170%] opacity-0"
                leave-to-class="transform translate-y-[170%] opacity-0"
                key="crm-menu-icon"
            >
                <Grid3x2SVG v-if="!visibleCRMActions && !rowEditMode" :fill="menuIconFill" iconClass="h-3 w-3" class="absolute right-3 bottom-1.5" />
            </Transition>
        </td>
        <td 
            class="border-r border-b border-gray-200 align-top relative bg-inherit"
            @mouseenter="showContactActions(actionDelay)"
            @mouseleave="hideContactActions"
            :style="contactsOuterCellStyle"
        >
            <div 
                @click="showContactActions"
                class="w-full flex flex-col items-start justify-start" 
                :style="contactsInnerCellStyle"
            >
                <div v-if="primaryContacts.length > 0" class="w-full overflow-x-clip">
                    <Transition
                        class="duration-300 ease-out"
                        :enter-from-class="contactCardEnterFromClass"
                        :leave-to-class="contactCardLeaveToClass"
                        enter-active-class="bg-gray-200"
                        leave-active-class="bg-gray-200"
                        key="contact-card"
                    >
                        <ContactCard
                            class="rounded-md"
                            v-if="primaryContact"
                            :contact="primaryContact"
                            :company="company"
                            :expandedRow="expandedRow"
                        />
                    </Transition>
                </div>
                <div v-else class="tracking-wide">
                    No Primary Contacts
                </div>
            </div>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform -translate-y-full opacity-0"
                leave-to-class="transform -translate-y-full opacity-0"
                key="contact-actions-meny"
            >
                <div v-if="visibleContactActions || rowEditMode"
                    ref="contactActionsMenuBoundary"
                    :class="rowEditMode ? 'w-full flex flex-wrap justify-end gap-1.5 px-2 py-2 pt-3 border-t border-gray-200' : 'w-[calc(100%+6px)] flex flex-wrap justify-end gap-1.5 shadow-md rounded-b-lg px-2 py-2 absolute -left-[3px] z-10 bg-inherit border-b-[3px] border-x-[3px] border-[#9EA0BD]'"
                    :style="contactActionsMenuStyle"
                >
                    <div v-if="primaryContacts.length > 1" class="flex space-x-1">
                        <button
                            class=" hover:bg-gray-200 h-[26px] 2xl:h-[30px] cursor-pointer text-sm 2xl:text-base rounded-md px-2.5 py-0.5 underline"
                            @click="getPrevPrimaryContact"
                            @mouseenter="blockExpandRow"
                            @mouseleave="unblockExpandRow"
                        >
                            &lt;
                        </button>
                        <div class="flex items-center px-1">
                            {{ primaryContactNumber }} of {{ numPrimaryContacts }}  
                        </div>
                        <button
                            class=" hover:bg-gray-200 h-[26px] 2xl:h-[30px] cursor-pointer text-sm 2xl:text-base rounded-md px-2.5 py-0.5 underline"
                            @click="getNextPrimaryContact"
                            @mouseenter="blockExpandRow"
                            @mouseleave="unblockExpandRow"
                        >
                            &gt;
                        </button>
                    </div>
                    <div v-else-if="primaryContacts.length > 0" class="flex space-x-1">
                        <button
                            class=" h-[26px] 2xl:h-[30px] text-gray-400 bg-gray-200 text-sm 2xl:text-base rounded-md px-2.5 py-0.5 underline"
                            disabled
                            @mouseenter="blockExpandRow"
                            @mouseleave="unblockExpandRow"
                        >
                            &lt;
                        </button>
                        <div class="flex items-center px-1">
                            {{ primaryContactNumber }} of {{ numPrimaryContacts }}  
                        </div>
                        <button
                            class=" h-[26px] 2xl:h-[30px] text-gray-400 bg-gray-200 text-sm 2xl:text-base rounded-md px-2.5 py-0.5 underline"
                            disabled
                            @mouseenter="blockExpandRow"
                            @mouseleave="unblockExpandRow"
                        >
                            &gt;
                        </button>
                    </div>
                    <div class="">
                        <button 
                            @click="performRowAction({actionKey: 'openAllContacts'})"
                            @mouseenter="blockExpandRow"
                            @mouseleave="unblockExpandRow"
                            class="w-28  bg-none border border-dark-secondary hover:bg-dark-primary hover:text-gray-100 hover:shadow active:bg-dark-primary active:text-gray-400 text-white bg-dark-secondary whitespace-nowrap tracking-wide font-medium px-1 py-1 rounded-md"
                        >
                            All Contacts
                        </button>
                        </div>
                    <div class="">
                        <button 
                            @click="performRowAction({actionKey: 'addContact'})"
                            @mouseenter="blockExpandRow"
                            @mouseleave="unblockExpandRow"
                            class="w-8 h-[26px] 2xl:h-[29px] text-sm 2xl:text-base bg-none border border-dark-secondary hover:bg-dark-primary hover:text-gray-100 hover:shadow active:bg-dark-primary active:text-gray-400 text-white bg-dark-secondary whitespace-nowrap tracking-wide font-medium rounded-md">
                            +
                        </button>
                        </div>
                </div>
            </Transition>
            <Transition
                class="duration-300 ease-out"
                enter-from-class="transform translate-y-[170%] opacity-0"
                leave-to-class="transform translate-y-[170%] opacity-0"
                key="contact-menu-icon"
            >
                <Grid3x2SVG v-if="!visibleContactActions && !rowEditMode" :fill="menuIconFill" iconClass="h-3 w-3" class="absolute right-3 bottom-1.5" />
            </Transition>
        </td>
    </TableRow>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed, onMounted } from 'vue';
import ExpandSVG from '../svg-components/ExpandSVG.vue';
import EditSVG from '../svg-components/EditSVG.vue';
import CheckMarkSVG from '../svg-components/CheckMarkSVG.vue';
import LoadingButton from '../base-components/LoadingButton.vue';
import DisabledButton from '../base-components/DisabledButton.vue';
import TableRow from '../base-components/TableRow.vue';
import CRMDetailsList from '@/components/company/CRMDetailsList.vue';
import ContactCard from './ContactCard.vue';
import ClientFeedbackHistory from './ClientFeedbackHistory.vue';
import ClientFeedbackNotesList from '../company-cell-components/ClientFeedbackNotesList.vue';
import Grid3x2SVG from '../svg-components/Grid3x2SVG.vue';
import ArchiveSVG from '../svg-components/ArchiveSVG.vue';
import BanSVG from '../svg-components/BanSVG.vue';
import CoinSVG from '../svg-components/CoinSVG.vue';
import EyeglassesSVG from '../svg-components/EyeglassesSVG.vue';
import ChevronDownMiniSVG from '../svg-components/ChevronDownMiniSVG.vue';
import ChevronUpMiniSVG from '../svg-components/ChevronUpMiniSVG.vue';
import MissingWebTextSVG from '../svg-components/MissingWebTextSVG.vue';
import SuccessfulWebTextSVG from '../svg-components/SuccessfulWebTextSVG.vue';
import FailedWebTextSVG from '../svg-components/FailedWebTextSVG.vue';
import LocationSuccesSVG from '../svg-components/LocationSuccesSVG.vue';
import LocationFailSVG from '../svg-components/LocationFailSVG.vue';
import LocationNoneSVG from '../svg-components/LocationNoneSVG.vue';
import { formatDate, formatDateTwoDigitYear } from '@/composables/formatDate';
import { getDateAsString } from '@/composables/formatDate';

const props = defineProps([
    'company',
    'selectAll',
    'expandAll',
    'clearedSelectedResults',
    'crmStatusOptions',
    'crmNextActionOptions',
    'resetToPreviousCRMAction',
    'actionButtonResetId',
    'selectedResults',
    'rowEditMode',
    'companyEdits',
    'isEdited',
    'campaigns'
])

const emit = defineEmits([
    'open-row-details',
    'select-row',
    'deselect-row',
    'update-crm-status',
    'perform-row-action',
    'field-edit',
    'toggle-row-edit',
])

function getEditValue(field, fallback) {
    if (props.companyEdits && props.companyEdits[field] !== undefined) {
        return props.companyEdits[field]
    }
    return fallback
}

function emitFieldEdit(field, newValue, originalValue) {
    emit('field-edit', { companyId: props.company.id, field, newValue, originalValue, company: props.company })
}

const isSelected = ref(props.selectAll || props.selectedResults.includes(props.company.id))
const expandedRow = ref(false)
const rowHover = ref(false)
const canSetRowHover = ref(false)
const canExpandRow = ref(true)
const haltEmit = ref(false)

const isNamePopoverVisible = ref(false)
const xPosition = ref(0)
const yPosition = ref(0)
const nameElement = ref(null)

const campaignOptions = computed(() => {
    return props.campaigns.filter((campaign) => {
        return campaign.status == 'active'
    })
})

const crmDetailsEndIndex = ref(2)

const crmNextActionDueDate = ref(props.company.crm_next_action_due_date ? props.company.crm_next_action_due_date : null)

const canShowCompanyInfoActions = ref(false)
const visibleCompanyInfoActions = ref(false)

const canShowBusinessInfoActions = ref(false)
const visibleBusinessInfoActions = ref(false)

const canShowCampaignActions = ref(false)
const visibleCampaignActions = ref(false)

const canShowCRMActions = ref(false)
const visibleCRMActions = ref(false)
const dueDatePickerFocused = ref(false)

const canShowContactActions = ref(false)
const visibleContactActions = ref(false)

const contactCardEnterFromClass = ref('transform translate-x-[160%] opacity-0')
const contactCardLeaveToClass = ref('transform -translate-x-[160%] opacity-0')

const rowBoundary = ref(null)
const companyInfoActionsMenuBoundary = ref(null)
const businessInfoActionsMenuBoundary = ref(null)
const campaignActionsMenuBoundary = ref(null)
const crmActionsCell = ref(null)
const crmActionsMenuBoundary = ref(null)
const contactActionsMenuBoundary = ref(null)
const tierLastUpdated = computed(() => {
        if(props.company.tier_last_updated_date){
            const updatedDate = new Date(props.company.tier_last_updated_date).toLocaleDateString("en-US")
            return updatedDate
        } else {
            return "--"
        }
    })
const initialRowHeight = ref(null)

const crmStatus = ref(
    props.company.campaign && 
    props.company.crm_statuses.length > 0 && 
    props.company.crm_statuses.filter(status => status.campaign.id == props.company.campaign.id).length > 0 ?
    props.company.crm_statuses.filter(status => status.campaign.id == props.company.campaign.id).reduce((accumulator, currentValue) => {
        let accDate = new Date(accumulator.created_on).getTime()
        let currDate = new Date(currentValue.created_on).getTime()
        if(accDate > currDate){
            return accumulator
        } else {
            return currentValue
        }
    }).crm_status.id : 
    null
)
const previousAction = ref(props.company.crm_next_action ? props.company.crm_next_action : null)
const crmNextAction = ref(props.company.crm_next_action ? props.company.crm_next_action : null)

watch(() => props.company.crm_next_action, () => {
    previousAction.value = props.company.crm_next_action
})

watch(() => props.resetToPreviousCRMAction, () => {
    // console.log('watch reset to prev', crmNextAction.value, previousAction.value)
    if(crmNextAction.value != previousAction.value) {
        haltEmit.value = true
        crmNextAction.value = previousAction.value
        setTimeout(() => {
            haltEmit.value = false
        }, 100)

    }
})

const markedURLVetted = computed(() => {
    return props.company.url_vetted_by && props.company.url_vetted_by.id ? true : false;
})

const hasURL = computed(() => {
    return props.company.website_url ? true : false;
})

const isAcquired = computed(() => {
    return props.company.acquirer
})

const pendingURLVetAction = computed(() => props.companyEdits?.url_vet_action ?? null)

const effectiveURLVetted = computed(() => {
    if (pendingURLVetAction.value === 'mark_url_vetted') return true
    if (pendingURLVetAction.value === 'remove_url_vetted' || pendingURLVetAction.value === 'mark_no_url_vetted') return false
    return markedURLVetted.value
})

const effectiveHasURL = computed(() => {
    if (pendingURLVetAction.value === 'mark_no_url_vetted') return false
    return hasURL.value
})

function handleURLVetClick(action) {
    if (pendingURLVetAction.value === action) {
        emitFieldEdit('url_vet_action', null, null)
        return
    }
    const wouldRestoreOriginal =
        (action === 'mark_url_vetted' && markedURLVetted.value) ||
        (action === 'remove_url_vetted' && !markedURLVetted.value) ||
        (action === 'mark_no_url_vetted' && isNoURL.value)
    emitFieldEdit('url_vet_action', wouldRestoreOriginal ? null : action, null)
}

const companyHasZI = computed(() => {
    return props.company.zoom_info_company && props.company.zoom_info_company.id ? true : false
})


const primaryContacts = computed(() => {
    return props.company.contacts.filter((contact) => contact.is_primary_contact)
    // if(props.company.contacts){
    //     let primaryContactsList = []
    //     props.company.contacts.forEach((contact) => {
    //         // for(let i = 0; i < contact.company_relationships.length; i++){
    //         //     if(contact.company_relationships[i].company_id == props.company.id && contact.company_relationships[i].is_primary_contact){
    //         //         primaryContactsList.push(contact)
    //         //     }
    //         // }
    //     })
    //     return primaryContactsList
    // } else {
    //     return []
    // }
})

watch(() => primaryContacts.value, () => {primaryContacts.value.length > 0 ? primaryContact.value = primaryContacts.value[0] : primaryContact.value = null}, {deep: true})

const numPrimaryContacts = computed(() => {
    return primaryContacts.value.length
})

const primaryContact = ref(primaryContacts.value.length > 0 ? primaryContacts.value[0] : null)
const primaryContactNumber = ref(primaryContacts.value.length > 0 ? 1 : null)

const companyAcquirer = computed(() => {
    return props.company.acquirer ? props.company.acquirer : null;
})

const reasonCannotAcquire = computed(() => {
    return props.company.reason_cannot_acquire && props.company.reason_cannot_acquire != 'null' ? props.company.reason_cannot_acquire : null;
})

const archiveReason = computed(() => {
    return props.company.archive_reason && props.company.archive_reason != 'null' ? props.company.archive_reason : null;
})

const furtherResearchNeeded = computed(() => {
    return props.company.further_research_needed ? props.company.further_research_needed : null;
})

const webTextStatus = computed(() => {
    if(props.company.website_texts.length == 0){
        return 'none'
    } else {
        if(props.company.website_texts[0].crawl_status == 'failed'){
            return 'failed'
        } else {
            return 'success'
        }
    }
    // if(props.company.web_texts.length == 0){
    //     return 'none'
    // } else {
    //     if(props.company.web_texts[0].crawl_status == 'failed'){
    //         return 'failed'
    //     } else {
    //         return 'success'
    //     }
    // }
})

const locationStatus = computed(() => {
    if(props.company.locations.length==0) {
        return 'none'
    } else{
        let locations = props.company.locations
        let mostRecentLocation = locations.sort((a, b) => { return new Date(b.created_at) - new Date(a.created_at)})[0]
        if(mostRecentLocation.lat==null) {
            return 'failed'
        } else {
            return 'success'
        }
    }
})

const previousCampaigns = computed(() => {
    return props.company.previous_campaigns
})

const webTextDate = computed(() => {
    if(props.company.website_texts.length == 0){
        return ''
    } else {
        if(props.company.website_texts[0].crawl_date){
            return ` on ${formatDateTwoDigitYear(props.company.website_texts[0].crawl_date)}`
        } else {
            return ''
        }
    }
    // if(props.company.web_texts.length == 0){
    //     return ''
    // } else {
    //     if(props.company.web_texts[0].crawl_date){
    //         return ` on ${formatDateTwoDigitYear(props.company.web_texts[0].crawl_date)}`
    //     } else {
    //         return ''
    //     }
    // }
})

const successCrawlString = computed(() => {
    return `Company website successfully crawled${webTextDate.value}. Available for full text search.`
})

const acquisitionInformation = computed(() => {
    let acqInfo = ''
    if(props.company.acquirer) {
        acqInfo += `Acquirer: ${props.company.acquirer}\n`
    }
    if(props.company.acquisition_date) {
        acqInfo += `Date: ${formatDate(props.company.acquisition_date)}`
    }
    return acqInfo
})

onMounted(() => {
    initialRowHeight.value = Math.round(rowBoundary.value.getBoundingClientRect().bottom - rowBoundary.value.getBoundingClientRect().top).toString()
})

watch(() => props.selectAll, () => {
    isSelected.value = props.selectAll
    if(props.selectAll) {
        emit('select-row',props.company)
    } else if (!props.selectAll) {
        emit('deselect-row', props.company)
    }
})

watch(() => props.expandAll, () => {
    // console.log('watch expandAll', props.expandAll)
    if(props.expandAll == 'expand') {
        expandRow()
    } else if (props.expandAll == 'collapse') {
        collapseRow()
    }
})

watch(() => props.clearedSelectedResults, () => {
    // console.log("clearedSelectedResults changed")
    isSelected.value = false
})

// crmStatus watcher removed — changes tracked via emitFieldEdit instead of immediate backend call

// crmNextAction watcher removed — changes tracked via emitFieldEdit instead of immediate backend call

const statusOptions = computed(() => {
    return [
        ...props.crmStatusOptions.map((status)=>{
            return {'value': status.value, 'label': status.label}
        })
    ]
})

const actionDelay = computed(() => {
    if(expandedRow.value || rowHover.value) {
        return 150
    } else {
        return 1250
    }
})

const rowActionsOuterCellStyle = computed(() => {
    return ''
})

const rowActionsInnerCellStyle = computed(() => {
    return ''
})

const companyInfoOuterCellStyle = computed(() => {
    if(props.rowEditMode) return ''
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleCompanyInfoActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const companyInfoInnerCellStyle = computed(() => {
    if(props.rowEditMode) return 'padding: 28px 12px 6px 12px;'
    let innerCellStyle = ''
    if(visibleCompanyInfoActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const businessInfoOuterCellStyle = computed(() => {
    if(props.rowEditMode) return ''
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleBusinessInfoActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const businessInfoInnerCellStyle = computed(() => {
    if(props.rowEditMode) return 'padding: 28px 12px 6px 12px;'
    let innerCellStyle = ''
    if(visibleBusinessInfoActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const campaignOuterCellStyle = computed(() => {
    if(props.rowEditMode) return ''
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleCampaignActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const campaignInnerCellStyle = computed(() => {
    if(props.rowEditMode) return 'padding: 28px 12px 6px 12px;'
    let innerCellStyle = ''
    if(visibleCampaignActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const crmActionsOuterCellStyle = computed(() => {
    if(props.rowEditMode) return ''
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleCRMActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const crmActionsInnerCellStyle = computed(() => {
    if(props.rowEditMode) return 'padding: 28px 12px 6px 12px;'
    let innerCellStyle = ''
    if(visibleCRMActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const contactsOuterCellStyle = computed(() => {
    if(props.rowEditMode) return ''
    let outerCellStyle = ''
    if(expandedRow.value) {
        outerCellStyle += 'height: 100%; '
    } else {
        outerCellStyle += `height: ${initialRowHeight.value}px; `
    }
    if(visibleContactActions.value) {
        outerCellStyle += `border-top: 3px solid #9EA0BD; border-right: 3px solid #9EA0BD; border-bottom: none; border-left: 3px solid #9EA0BD; border-top-left-radius: 8px; border-top-right-radius: 8px;`
    }
    return outerCellStyle
})

const contactsInnerCellStyle = computed(() => {
    if(props.rowEditMode) return 'padding: 28px 12px 6px 12px;'
    let innerCellStyle = ''
    if(visibleContactActions.value) {
        innerCellStyle += 'padding: 25px 9px 3px 9px;'
    } else {
        innerCellStyle += 'padding: 28px 12px 6px 12px;'
    }
    return innerCellStyle
})

const companyInfoActionsMenuStyle = computed(() => {
    if(props.rowEditMode) return ''
    if(visibleCompanyInfoActions.value && companyInfoActionsMenuBoundary.value) {
        let menuHeight = Math.round(companyInfoActionsMenuBoundary.value.getBoundingClientRect().bottom - companyInfoActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const businessInfoActionsMenuStyle = computed(() => {
    if(props.rowEditMode) return ''
    if(visibleBusinessInfoActions.value && businessInfoActionsMenuBoundary.value) {
        let menuHeight = Math.round(businessInfoActionsMenuBoundary.value.getBoundingClientRect().bottom - businessInfoActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const campaignActionsMenuStyle = computed(() => {
    if(props.rowEditMode) return ''
    if(visibleCampaignActions.value && campaignActionsMenuBoundary.value) {
        let menuHeight = Math.round(campaignActionsMenuBoundary.value.getBoundingClientRect().bottom - campaignActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const crmActionsMenuStyle = computed(() => {
    if(props.rowEditMode) return ''
    if(visibleCRMActions.value && crmActionsMenuBoundary.value) {
        let menuHeight = Math.round(crmActionsMenuBoundary.value.getBoundingClientRect().bottom - crmActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const contactActionsMenuStyle = computed(() => {
    if(props.rowEditMode) return ''
    if(visibleContactActions.value && contactActionsMenuBoundary.value) {
        let menuHeight = Math.round(contactActionsMenuBoundary.value.getBoundingClientRect().bottom - contactActionsMenuBoundary.value.getBoundingClientRect().top-1).toString()
        return `bottom: -${menuHeight}px;`
    }
    return ''
})

const companyName = computed(() => {
    return getEditValue('company_name', props.company.company_name) || '';
})

const companyDBA = computed(() => {
    return getEditValue('doing_business_as', props.company.doing_business_as) || '';
})

const isNoURL = computed(() => {
    const url = getEditValue('website_url', props.company.website_url)
    return url?.includes('no-url-') ?? false
})

const effectiveIsNoURL = computed(() => isNoURL.value || pendingURLVetAction.value === 'mark_no_url_vetted')

const companyWebsiteURL = computed(() => {
    const url = getEditValue('website_url', props.company.website_url)
    if (url) {
        if (url.startsWith('http://') || url.startsWith('https://')) {
            return url
        } else {
            return `http://${url}`
        }
    }
    return null
})

const companyZIURL = computed(() => {
    if(props.company.zoom_info_company && props.company.zoom_info_company.website) {
        if (props.company.zoom_info_company.website.startsWith('http://') || props.company.zoom_info_company.website.startsWith('https://')) {
            return props.company.zoom_info_company.website
        } else {
            return `http://${props.company.zoom_info_company.website}`
        }
    }
    return null
})

const companyAddressLineOne = computed(() => {
    return getEditValue('address', props.company.address) || null
})

const companyAddressLineTwo = computed(() => {
    let addressLineTwo = ''
    const city = getEditValue('city', props.company.city)
    const state = getEditValue('state', props.company.state)
    const zip = getEditValue('zip', props.company.zip)
    const country = getEditValue('country', props.company.country)
    if (city) addressLineTwo += `${city}, `
    if (state) addressLineTwo += `${state} `
    if (zip) addressLineTwo += `${zip} `
    if (country) addressLineTwo += `${country} `
    return addressLineTwo.length > 0 ? addressLineTwo : null
})

const ziAddressLineOne = computed(() => {
    return props.company.zoom_info_company && props.company.zoom_info_company.street ? props.company.zoom_info_company.street  : null
})

const ziAddressLineTwo = computed(() => {
    let addressLineTwo = ''
    if(props.company.zoom_info_company) {
        if(props.company.zoom_info_company.city) {
            addressLineTwo += `${props.company.zoom_info_company.city}, `
        }
        if(props.company.zoom_info_company.state) {
            addressLineTwo += `${props.company.zoom_info_company.state} `
        }
        if(props.company.zoom_info_company.zip_code) {
            addressLineTwo += `${props.company.zoom_info_company.zip_code} `
        }
        if(props.company.zoom_info_company.country) {
            addressLineTwo += `${props.company.zoom_info_company.country} `
        }
        if(addressLineTwo.length > 0) {
            return addressLineTwo
        }
    }
    return null
})

const companyRevenue = computed(() => {
    const val = getEditValue('revenue', props.company.revenue)
    return val ? `$${val} M` : '';
})

const companyHeadcount = computed(() => {
    const val = getEditValue('employee_headcount', props.company.employee_headcount)
    return val ? `${val} ppl` : '';
})

const accountManagerNotes = computed(() => {
    return props.company.account_manager_research_notes ? props.company.account_manager_research_notes : null;
})

const companyParentCompany = computed(() => {
    return getEditValue('parent_company', props.company.parent_company) || '';
})

const companyInvestors = computed(() => {
    return getEditValue('investors', props.company.investors) || '';
})

const zoomInfoId = computed(() => {
    return props.company.zoom_info_company ? props.company.zoom_info_company.zoom_info_id : '';
})

const zoomInfoRevenue = computed(() => {
    return props.company.zoom_info_company && props.company.zoom_info_company.revenue ? `$${props.company.zoom_info_company.revenue/1000} M` : '';
})

const zoomInfoHeadcount = computed(() => {
    return props.company.zoom_info_company && props.company.zoom_info_company.employee_count ? `${props.company.zoom_info_company.employee_count} ppl` : '';
})

const companyCampaign = computed(() => {
    return props.company.campaign ? props.company.campaign.campaign_name : '';
})

const campaignChanged = computed(() => props.companyEdits != null && 'campaign_id' in props.companyEdits)

const requestedCampaign = computed(() => {
    if (!campaignChanged.value) return null
    const id = props.companyEdits?.campaign_id
    return props.campaigns?.find(c => c.id === id) ?? null
})

const pendingTier = computed(() =>
    (props.companyEdits && 'tier' in props.companyEdits) ? props.companyEdits.tier : undefined
)

const effectiveTier = computed(() =>
    pendingTier.value !== undefined ? pendingTier.value : (props.company.tier_object?.id ?? null)
)

const companyTier = computed(() => {
    switch(effectiveTier.value) {
        case 1: return '1'
        case 2: return '2'
        case 3: return '3'
        case 4: return '4'
        case 5: return '5 (Review)'
        default: return ''
    }
})

function handleTierClick(tier) {
    const originalTier = props.company.tier_object?.id ?? null
    emitFieldEdit('tier', tier === 'None' ? null : tier, originalTier)
}

const currentCRMNextAction = computed(() => {
    return getEditValue('crm_next_action', props.company.crm_next_action) || ''
})

const currentCRMNextActionDueDate = computed(() => {
    const date = getEditValue('crm_next_action_due_date', props.company.crm_next_action_due_date)
    return date ? `by ${formatDate(date)}` : null
})

const crmDetails = computed(() => {
    return props.company.crm_actions && props.company.campaign && props.company.crm_actions.filter((crmAction) => crmAction.campaign_id == props.company.campaign.id).length > 0 ? props.company.crm_actions : null
})

const numCRMDetailsFromPreviousCampaigns = computed(() => {
    const campaign_id = props.company.campaign ? props.company.campaign.id : null
    return props.company.crm_actions ? props.company.crm_actions.filter((crmAction) => crmAction.campaign_id != campaign_id).length : 0
})

const clientFeedback = computed(() => {
    // The latest feedback recorded for the company's current client
    return getEditValue('client_feedback', props.company.current_client_feedback) || '';
})

// Every note for the client of the company's current campaign
const hasClientFeedbackNotes = computed(() => (props.company.current_client_feedback_notes ?? []).length > 0)

function reloadCompanies() {
    emit('perform-row-action', { actionKey: 'reloadCompanies' })
}

const relevantBusiness = computed(() => {
    return getEditValue('relevant_business', props.company.relevant_business) || null;
})

const clientUpdateNotes = computed(() => {
    return getEditValue('client_update_notes', props.company.client_update_notes) || null;
})

const outreachNotes = computed(() => {
    return props.company.outreach_notes ? props.company.outreach_notes : null;
})

const currentCRMStatus = computed(() => {
    if (props.companyEdits && 'crm_status' in props.companyEdits) {
        const pendingId = props.companyEdits.crm_status
        if (!pendingId) return ''
        const option = statusOptions.value.find(o => o.value == pendingId)
        return option ? option.label : ''
    }
    if(props.company.campaign){
        if(props.company.crm_statuses.length > 0){
            let crmStatuses = props.company.crm_statuses.filter(status => status.campaign.id == props.company.campaign.id)
            if(crmStatuses.length < 1){
                return ''
            }
            let currentStatus = crmStatuses.reduce((accumulator, currentValue) => {
                let accDate = new Date(accumulator.created_on).getTime()
                let currDate = new Date(currentValue.created_on).getTime()
                if(accDate > currDate){
                    return accumulator
                } else {
                    return currentValue
                }
            })
            return `${currentStatus.crm_status.status_name} ${new Date(currentStatus.created_on).toLocaleDateString()}`
        } else {
            return ''
        }
    } else{
        return ''
    }
})

const menuIconFill = computed(() => {
    return '#99a1af'
})

function blockExpandRow() {
    canExpandRow.value = false
}

function unblockExpandRow() {
    canExpandRow.value = true
}

function showCompanyInfoActions(delay=null) {
    if(props.rowEditMode) return
    canShowCompanyInfoActions.value = true
    if(delay) {
        setTimeout(() => {
            if(canShowCompanyInfoActions.value) {
                visibleCompanyInfoActions.value = true
                rowHover.value = true
            }
        }, delay)
    } else {
        visibleCompanyInfoActions.value = true
        rowHover.value = true
    }
}

function showBusinessInfoActions(delay=null) {
    if(props.rowEditMode) return
    canShowBusinessInfoActions.value = true
    if(delay) {
        setTimeout(() => {
            if(canShowBusinessInfoActions.value) {
                visibleBusinessInfoActions.value = true
                rowHover.value = true
            }
        }, delay)
    } else {
        visibleBusinessInfoActions.value = true
        rowHover.value = true
    }
}

function hideCompanyInfoActions() {
    if(props.rowEditMode) return
    canShowCompanyInfoActions.value = false
    visibleCompanyInfoActions.value = false
}

function hideBusinessInfoActions() {
    if(props.rowEditMode) return
    canShowBusinessInfoActions.value = false
    visibleBusinessInfoActions.value = false
}

function showCampaignActions(delay=null) {
    if(props.rowEditMode) return
    canShowCampaignActions.value = true
    if(delay) {
        setTimeout(() => {
            if(canShowCampaignActions.value) {
                visibleCampaignActions.value = true
                rowHover.value = true
            }
        }, delay)
    } else {
        visibleCampaignActions.value = true
        rowHover.value = true
    }

}

function hideCampaignActions() {
    if(props.rowEditMode) return
    canShowCampaignActions.value = false
    visibleCampaignActions.value = false
}

function showCRMActions(delay=null) {
    if(props.rowEditMode) return
    canShowCRMActions.value = true
    if(delay) {
        setTimeout(() => {
            if(canShowCRMActions.value) {
                visibleCRMActions.value = true
                rowHover.value = true
            }
        }, delay)
    } else {
        visibleCRMActions.value = true
        rowHover.value = true
    }

}

function hideCRMActions() {
    if(props.rowEditMode) return
    // The native date picker renders above the page, so moving onto it fires a
    // mouseleave on this cell even though the cursor never left the row. Ignore
    // that while the due date input still holds focus.
    if(dueDatePickerFocused.value) return
    canShowCRMActions.value = false
    visibleCRMActions.value = false
}

function handleDueDatePickerBlur() {
    dueDatePickerFocused.value = false
    // Catch up on any mouseleave that was swallowed while the picker was open.
    if(!crmActionsCell.value?.matches(':hover')) {
        hideCRMActions()
    }
}

function showContactActions(delay=null) {
    if(props.rowEditMode) return
    canShowContactActions.value = true
    if(delay) {
        setTimeout(() => {
            if(canShowContactActions.value) {
                visibleContactActions.value = true
                rowHover.value = true
            }
        }, delay)
    } else {
        visibleContactActions.value = true
        rowHover.value = true
    }
}

function hideContactActions() {
    if(props.rowEditMode) return
    canShowContactActions.value = false
    visibleContactActions.value = false
}

function getPrevPrimaryContact() {
    if(primaryContacts.value.length > 1) {
        let index = primaryContactNumber.value - 1
        if(index > 0) {
            contactCardEnterFromClass.value = 'transform -translate-x-[160%] opacity-0'
            contactCardLeaveToClass.value = 'transform translate-x-[160%] opacity-0'
            primaryContact.value = null
            setTimeout(() => {
                primaryContact.value = primaryContacts.value[index - 1]
                primaryContactNumber.value -= 1
            }, 50)

        }
    }
}

function getNextPrimaryContact() {
    if(primaryContacts.value.length > 1) {
        let index = primaryContactNumber.value - 1
        if(index < primaryContacts.value.length - 1) {
            contactCardEnterFromClass.value = 'transform translate-x-[200%] opacity-0'
            contactCardLeaveToClass.value = 'transform -translate-x-[200%] opacity-0'
            primaryContact.value = null
            setTimeout(() => {
                primaryContact.value = primaryContacts.value[index + 1]
                primaryContactNumber.value += 1
            }, 50)

        }
    }
}

function expandRow() {
    expandedRow.value = true
    crmDetailsEndIndex.value = null
}

function collapseRow() {
    if(props.rowEditMode) return
    expandedRow.value = false
    crmDetailsEndIndex.value = 2
}

function toggleExpandRow() {
    if(props.rowEditMode) return
    if(canExpandRow.value) {
        if(expandedRow.value) {
            collapseRow()
        } else {
            expandRow()
        }
    }
}

function handleRowEnter(delay) {
    canSetRowHover.value = true
    if(delay) {
        setTimeout(() => {
            if(canSetRowHover.value) {
                rowHover.value = true
            }
        }, delay)
    } else {
        expandRow()
    }
}

function handleRowLeave() {
    if(props.rowEditMode) return
    canSetRowHover.value = false
    rowHover.value = false
}

function toggleSelected() {
    if(isSelected.value) {
        isSelected.value = false
        emit('deselect-row', props.company)
    } else {
        isSelected.value = true
        emit('select-row', props.company)
    }
}

function emitDueDateOffset(days) {
    const today = new Date()
    const newDate = getDateAsString(new Date(today.setDate(today.getDate() + days)))
    emitFieldEdit('crm_next_action_due_date', newDate, props.company.crm_next_action_due_date)
}

const tierElement = ref(null)
const tierPopoverVisible = ref(false)

const campaignChangeRequestElement = ref(null)
const campaignChangeRequestPopoverVisible = ref(false)

function showCampaignChangeRequestPopover() {
    campaignChangeRequestPopoverVisible.value = true
    xPosition.value = campaignChangeRequestElement.value.getBoundingClientRect().right
    yPosition.value = campaignChangeRequestElement.value.getBoundingClientRect().top
}

function hideCampaignChangeRequestPopover() {
    campaignChangeRequestPopoverVisible.value = false
    xPosition.value = 0
    yPosition.value = 0
}

  function showTierPopover() {
            tierPopoverVisible.value = true;
            xPosition.value = tierElement.value.getBoundingClientRect().right
            yPosition.value = tierElement.value.getBoundingClientRect().top
          }

  function hideTierPopover() {
            tierPopoverVisible.value = false;
            xPosition.value = 0;
            yPosition.value = 0;
          }


function showPopover() {
    isNamePopoverVisible.value = true;
    xPosition.value = nameElement.value.getBoundingClientRect().right
    yPosition.value = nameElement.value.getBoundingClientRect().top
}

function hidePopover() {
    isNamePopoverVisible.value = false;
    xPosition.value = 0;
    yPosition.value = 0;
}


function performRowAction(actionData) {
    actionData.company = props.company
    // console.log('performRowAction', actionData)
    emit('perform-row-action', actionData)
}
</script>